import { operatorQueryKey } from '@features/auth';
import { employeeSessionQueryKey } from '@features/employee-session';
import { refusalKeys } from '@features/unidentified-refusals';

import type { QueryKey } from '@tanstack/react-query';

/**
 * Every cache an expired or revoked session can leave holding someone else's
 * data: the operator, the employee-pool session (its permissions gate the
 * Security nav entry), and the unidentified-refusals PII (IPs, domains, trace
 * ids). All three are reset together on any `UnauthenticatedError`, for the
 * same reason the operator cache alone used to be: a tab left open overnight
 * must not go on serving a lapsed session's data to whoever uses it next.
 */
const SESSION_SCOPED_QUERY_KEYS: readonly QueryKey[] = [
    operatorQueryKey,
    employeeSessionQueryKey,
    refusalKeys.all,
];

/** Whether resetting `key` would also reset (and refetch) `queryKey` — the same match `resetQueries` uses. */
function isUnder(key: QueryKey, queryKey: QueryKey): boolean {
    return (
        key.length <= queryKey.length && key.every((segment, index) => segment === queryKey[index])
    );
}

/**
 * Which session-scoped caches an `UnauthenticatedError` should clear.
 *
 * `failedQueryKey`, when given, is the query whose own error is being
 * handled — and is left out. Resetting it too would refetch it (it is still
 * mounted and the error that just arrived is the answer it was asking for),
 * 401 again, and spin: the same loop `QueryProvider` used to guard against
 * for the operator cache alone. `DashboardLayout`/`Sidebar`/the refusals
 * viewer each read their own query's error state directly, which is how a
 * session-less screen still renders correctly without needing a reset of
 * itself.
 */
export function sessionKeysToReset(failedQueryKey?: QueryKey): readonly QueryKey[] {
    return SESSION_SCOPED_QUERY_KEYS.filter(
        (key) => !(failedQueryKey && isUnder(key, failedQueryKey)),
    );
}
